import { Button } from "components/Button/Button";
import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { SubmitLock } from "components/Form/components/SubmitLock/SubmitLock";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Form } from "components/Form/components/Form/Form";
import { SwitchFormComponent } from "components/Form/components/Switch/SwitchFormComponent";
import { requiredValidator } from "components/Form/Form.utils";
import { RecordPromptPopover } from "components/Prompt/RecordPromptPopover";
import { create } from "containers/Auth/screens/IdentityManagement/IdentityRole.service";
import { AddButton } from "containers/Resources/screens/ResourcesSingle/components/SubrecordTable";
import { Identity_Role } from "graphql/generated/graphql";
import { useState } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { useDispatch } from "react-redux";
import { roleIdentityPickerQueryField } from "./constants";

interface RoleIdentityFormProps {
    closeModal: () => void;
    onSubmit: (formValues: { identityId: string; propagate: boolean }) => void;
    filterOptionList: (option: AutocompleteOption) => boolean;
}

export const RoleIdentityForm = ({ closeModal, onSubmit, filterOptionList }: RoleIdentityFormProps) => {
    return (
        <>
            <div className="recordPrompt__header">
                <span>Add identity to the role</span>
            </div>
            <FinalForm onSubmit={onSubmit} initialValues={{ identityId: null, propagate: false }}>
                {({ handleSubmit, pristine, hasValidationErrors, submitting }) => {
                    return (
                        <Form onSubmit={handleSubmit}>
                            <SubmitLock locked={submitting}>
                                <Form.Row>
                                    <Field
                                        name="identityId"
                                        id="identityId"
                                        component={AutocompleteFormComponent}
                                        fieldAttr={{ required: true }}
                                        fieldProps={{
                                            label: "Identity",
                                            options: {
                                                resource: "identity",
                                                queryField: roleIdentityPickerQueryField,
                                                filterOptions: filterOptionList,
                                            },
                                        }}
                                        validate={requiredValidator}
                                    />
                                </Form.Row>
                                <Form.Row>
                                    <Field
                                        name="propagate"
                                        id="propagate"
                                        fieldProps={{
                                            label: "Propagate",
                                        }}
                                        fieldAttr={{}}
                                        component={SwitchFormComponent}
                                    />
                                </Form.Row>
                            </SubmitLock>
                            <Form.Row>
                                <CancelButton
                                    onClick={() => closeModal()}
                                    disabled={submitting}
                                />
                                <Button
                                    submit
                                    variation="primary"
                                    label="Save"
                                    loading={submitting}
                                    disabled={pristine || hasValidationErrors || submitting}
                                />
                            </Form.Row>
                        </Form>
                    );
                }}
            </FinalForm>
        </>
    );
};

export const AddRoleIdentityPopover = ({
    isActionButtonDisabled,
    roleId,
    fetchResourceData,
    currentIdentityList,
}: {
    isActionButtonDisabled: boolean;
    roleId: string;
    fetchResourceData: () => void;
    currentIdentityList: Identity_Role[];
}) => {
    const dispatch = useDispatch();
    const [isOpened, setIsOpened] = useState(false);

    const onSubmit = (formValues) => {
        // Return the promise so react-final-form's `submitting` flag tracks the
        // whole request, driving the Save button's spinner + disable (and the
        // Cancel sibling's disable) while the create is in flight
        // (ui-design-principle-buttons: async-triggering in-progress feedback).
        return create(dispatch, formValues.identityId, roleId, formValues.propagate).then(() => {
            setIsOpened(false);
            fetchResourceData();
        });
    };

    const filterOptionList = ({ value }) =>
        !currentIdentityList.find(({ identity_id }) => identity_id === value);

    return (
        <>
            <AddButton
                onClick={() => setIsOpened(true)}
                isDisabled={isActionButtonDisabled}
                tooltip="Add identity to the role"
            />
            <RecordPromptPopover
                paperStyle={{ minWidth: "550px", overflow: "visible" }}
                isOpened={isOpened}
            >
                <RoleIdentityForm
                    onSubmit={onSubmit}
                    closeModal={() => setIsOpened(false)}
                    filterOptionList={filterOptionList}
                />
            </RecordPromptPopover>
        </>
    );
};
